import { toast } from '@/hooks/use-toast';
import { logger } from './logger';

// Tipos de errores
export enum ErrorType {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NETWORK = 'network',
  DATABASE = 'database',
  BUSINESS_LOGIC = 'business_logic',
  UNKNOWN = 'unknown'
}

export interface ErrorDetails {
  message: string;
  code?: string;
  component?: string;
  action?: string;
  userId?: string;
  timestamp: string;
}

export interface AppError {
  type: ErrorType;
  message: string;
  code?: string;
  details?: any;
  timestamp: Date;
  component?: string;
  action?: string;
  userId?: string;
}

export class AppErrorClass extends Error {
  public code?: string;
  public component?: string;
  public action?: string;
  public userId?: string;
  public isUserFacing: boolean;

  constructor(
    message: string, 
    options?: {
      code?: string;
      component?: string;
      action?: string;
      userId?: string;
      isUserFacing?: boolean;
    }
  ) {
    super(message);
    this.name = 'AppError';
    this.code = options?.code;
    this.component = options?.component;
    this.action = options?.action;
    this.userId = options?.userId;
    this.isUserFacing = options?.isUserFacing || false;
  }
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: AppError[] = [];
  private maxLogSize = 100;

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  // Manejo centralizado de errores
  handleError(error: any, context?: {
    component?: string;
    action?: string;
    userId?: string;
  }): AppError {
    const appError: AppError = {
      type: this.classifyError(error),
      message: this.extractMessage(error),
      code: error.code || error.name,
      details: error.details || error.stack,
      timestamp: new Date(),
      component: context?.component,
      action: context?.action,
      userId: context?.userId,
    };

    // Log del error
    this.logError(appError);

    // Mostrar toast al usuario si es apropiado
    this.showUserNotification(appError);

    // En desarrollo, también log a consola
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorHandler:', appError);
    }

    return appError;
  }

  handle(error: Error | AppErrorClass, component?: string): string {
    const errorDetails: ErrorDetails = {
      message: error.message,
      code: 'code' in error ? error.code : undefined,
      component: component || ('component' in error ? error.component : undefined),
      action: 'action' in error ? error.action : undefined,
      userId: 'userId' in error ? error.userId : undefined,
      timestamp: new Date().toISOString()
    };

    // Log the error
    logger.error(error.message, errorDetails, component);

    // Return user-friendly message
    if (error instanceof AppErrorClass && error.isUserFacing) {
      return error.message;
    }

    return this.getUserFriendlyMessage({
      type: this.classifyError(error),
      message: error.message,
      timestamp: new Date(),
      component
    });
  }

  async handleAsync<T>(
    asyncOperation: () => Promise<T>,
    component?: string,
    fallbackValue?: T
  ): Promise<T | undefined> {
    try {
      return await asyncOperation();
    } catch (error) {
      const userMessage = this.handle(error as Error, component);
      
      if (fallbackValue !== undefined) {
        return fallbackValue;
      }
      
      throw new AppErrorClass(userMessage, {
        component,
        isUserFacing: true
      });
    }
  }

  private classifyError(error: any): ErrorType {
    if (error.code) {
      if (error.code.includes('auth')) return ErrorType.AUTHENTICATION;
      if (error.code.includes('permission') || error.code === 'PGRST301') return ErrorType.AUTHORIZATION;
      if (error.code.includes('validation') || error.code === 'PGRST116') return ErrorType.VALIDATION;
      if (error.code.includes('connection') || error.code === 'PGRST301') return ErrorType.NETWORK;
    }

    if (error.name === 'NetworkError' || error.message?.includes('fetch') || error.message?.includes('Network')) {
      return ErrorType.NETWORK;
    }

    if (error.name === 'ValidationError' || error.message?.includes('required')) {
      return ErrorType.VALIDATION;
    }

    if (error.message?.includes('unauthorized') || error.message?.includes('not authenticated') || error.message?.includes('auth')) {
      return ErrorType.AUTHENTICATION;
    }

    if (error.message?.includes('rate limit') || error.message?.includes('too many')) {
      return ErrorType.AUTHORIZATION;
    }

    return ErrorType.UNKNOWN;
  }

  private extractMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.error_description) return error.error_description;
    if (error.hint) return error.hint;
    return 'Ha ocurrido un error inesperado';
  }

  private logError(error: AppError): void {
    this.errorLog.unshift(error);
    
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    if (process.env.NODE_ENV === 'production') {
      this.sendToExternalLogging(error);
    }
  }

  private showUserNotification(error: AppError): void {
    if (error.type === ErrorType.NETWORK || 
        error.type === ErrorType.AUTHENTICATION || 
        error.type === ErrorType.VALIDATION) {
      
      toast({
        title: this.getErrorTitle(error.type),
        description: this.getUserFriendlyMessage(error),
        variant: "destructive",
      });
    }
  }

  private getErrorTitle(type: ErrorType): string {
    switch (type) {
      case ErrorType.AUTHENTICATION:
        return 'Error de Autenticación';
      case ErrorType.AUTHORIZATION:
        return 'Sin Permisos';
      case ErrorType.VALIDATION:
        return 'Datos Inválidos';
      case ErrorType.NETWORK:
        return 'Error de Conexión';
      case ErrorType.DATABASE:
        return 'Error de Base de Datos';
      default:
        return 'Error';
    }
  }

  private getUserFriendlyMessage(error: AppError): string {
    switch (error.type) {
      case ErrorType.AUTHENTICATION:
        return 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      case ErrorType.AUTHORIZATION:
        return 'No tienes permisos para realizar esta acción';
      case ErrorType.VALIDATION:
        return 'Por favor, verifica los datos ingresados';
      case ErrorType.NETWORK:
        return 'Problema de conexión. Verifica tu internet e inténtalo de nuevo.';
      case ErrorType.DATABASE:
        return 'Error del servidor. Intenta más tarde';
      default:
        return 'Ocurrió un error inesperado. Inténtalo de nuevo.';
    }
  }

  private sendToExternalLogging(error: AppError): void {
    try {
      const existingLogs = localStorage.getItem('app_error_logs');
      const logs = existingLogs ? JSON.parse(existingLogs) : [];
      logs.unshift(error);
      
      if (logs.length > 50) {
        logs.slice(0, 50);
      }
      
      localStorage.setItem('app_error_logs', JSON.stringify(logs));
    } catch (e) {
      // Fail silently
    }
  }

  getErrorLog(): AppError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }

  reportError(error: any, context: {
    component: string;
    action: string;
    userId?: string;
  }): void {
    this.handleError(error, context);
  }

  reportCriticalError(error: any, context: {
    component: string;
    action: string;
    userId?: string;
  }): void {
    const appError = this.handleError(error, context);
    
    toast({
      title: '⚠️ Error Crítico',
      description: 'Ha ocurrido un error grave. Contacta al soporte si persiste.',
      variant: "destructive",
    });

    if (process.env.NODE_ENV === 'production') {
      console.error('CRITICAL ERROR:', appError);
    }
  }
}

// Singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Hook for React components
export const useErrorHandler = () => {
  const handleError = (error: Error | AppErrorClass, component?: string) => {
    const userMessage = errorHandler.handle(error, component);
    
    toast({
      title: "Error",
      description: userMessage,
      variant: "destructive",
    });
  };

  const handleAsyncError = async <T>(
    asyncOperation: () => Promise<T>,
    component?: string,
    showToast = true
  ): Promise<T | undefined> => {
    try {
      return await asyncOperation();
    } catch (error) {
      if (showToast) {
        handleError(error as Error, component);
      }
      return undefined;
    }
  };

  return { handleError, handleAsyncError };
};

// Helper functions
export const handleError = (error: any, context?: {
  component?: string;
  action?: string;
  userId?: string;
}) => errorHandler.handleError(error, context);

export const reportError = (error: any, context: {
  component: string;
  action: string;
  userId?: string;
}) => errorHandler.reportError(error, context);

export const reportCriticalError = (error: any, context: {
  component: string;
  action: string;
  userId?: string;
}) => errorHandler.reportCriticalError(error, context);
